import { aykId, article_order_status } from 'constant/static';
import { useGetCurrentUserQuery } from 'services';

import { useUserHasPermission } from './useUserHasPermission';

const EU_MANAGER = 'ROLE_EU_MANAGER';
const PUB_MANAGER = 'ROLE_PUB_MANAGER';

/**
 * @description Madde adını düzenleyebilmek için maddenin **o anki statüsünde** sahip olunması gereken roller:
 * `statü → yeterli rollerden herhangi biri`. Kuralın tek kaynağıdır; iş gerekçesi ve BE kontratı için bkz.
 * `docs/article-title-edit.md`.
 *
 * Yapı şu iki kuralın birleşimidir: birim yöneticisi kendi biriminin **her** aşamasında (AB → 27–38/51/53,
 * YM → 39–43/52), redaksiyon rolü ise yalnız **kendi** aşamasında düzenleyebilir. Aşama listesi BE'de atama
 * olarak da tutulur (`GET /assignment_fields?type=0` AB, `type=1` YM) — BE yeni bir aşama eklerse buraya da
 * eklenmelidir.
 *
 * Bu matriste yer almayan iki yetki ayrı kurulur: admin statüden bağımsız her aşamada düzenler; statü 44
 * (AYK) ise rolle değil komite üyeliğiyle çözülür (bkz. `useArticleTitleEditPermission`).
 */
const TITLE_EDIT_ROLE_MATRIX = {
  [article_order_status['Telif Sonrasi']]: [EU_MANAGER], // 27 — TS - AB Madde Atama
  [article_order_status['AB - Madde Başlığı Redaksiyonu']]: [EU_MANAGER, 'ROLE_POST_ORDER_TITLE_REDACTION'], // 28
  [article_order_status['AB - Ayet Hadis Kontrolü']]: [EU_MANAGER], // 30
  [article_order_status['AB - Yazma Eser Kontrolü']]: [EU_MANAGER], // 51
  [article_order_status['AB - Madde Özü Yazımı ve Redaksiyonu']]: [EU_MANAGER], // 31
  [article_order_status['AB - Kimlik Kartı Redaksiyonu']]: [EU_MANAGER], // 32
  [article_order_status['AB - Üst Veri ve Kavram Ağacı Redaksiyonu']]: [EU_MANAGER], // 33
  [article_order_status['AB - Üslup/Metin Redaksiyonu']]: [EU_MANAGER, 'ROLE_POST_ORDER_WORD_REDACTION'], // 34
  [article_order_status['AB - Metin İçi Veri ve Atıf-İlişkili Madde Kontrolü']]: [EU_MANAGER], // 35
  [article_order_status['AB - Medya Teklifi Kontrolü']]: [EU_MANAGER], // 36
  [article_order_status['Ansiklopedi Birimi Kontrol']]: [EU_MANAGER], // 37 — TS - AB Kontrol
  [article_order_status['TS - İH Madde Kontrol']]: [EU_MANAGER], // 38
  [article_order_status['TS - Madde Sorumlusu Kontrol']]: [EU_MANAGER], // 53
  [article_order_status['Yayin Mudurlugu Madde Atama']]: [PUB_MANAGER], // 39 — TS - YM Madde Atama
  [article_order_status['YM - Atıf-Kaynaklar Redaksiyonu']]: [PUB_MANAGER], // 40
  [article_order_status['YM - Çeviri-Yazı Redaksiyonu']]: [PUB_MANAGER, 'ROLE_POST_ORDER_TRANSLATION_TEXT_REDACTION'], // 41
  [article_order_status['Yayin Mudurlugu Imla Redaksiyon']]: [PUB_MANAGER, 'ROLE_POST_ORDER_ORTHOGRAPHY_REDACTION'], // 42
  [article_order_status['Yayin Mudurlugu Editoryal Son Okuma']]: [PUB_MANAGER, 'ROLE_POST_ORDER_PROOFREADING_CONTROL'], // 52
  [article_order_status['Yayin Mudurlugu Genel Kontrol']]: [PUB_MANAGER], // 43 — TS - YM Kontrol
};

/**
 * @typedef {Object} ArticleTitleEditPermission
 * @property {boolean} canEditTitle Madde adı (başlık) düzenleme yetkisi var mı — madde detay sayfasındaki
 *   başlık düzenleme butonunun tek karar noktası.
 */

/**
 * @description Madde adı düzenleme için "statü × rol" yetki katmanı. Maddenin `status`'üne ve current_user'ın
 * rollerine/komite üyeliğine göre başlığın düzenlenebilir olup olmadığını döner.
 *
 * Kuralın tek kaynağı bu dosyadır; iş gerekçesi ve BE kontratı için bkz. `docs/article-title-edit.md`.
 * Matris:
 * | Kim | Nerede |
 * |---|---|
 * | Admin / Süper Admin | **statüden bağımsız** bütün aşamalar |
 * | AB Yöneticisi (`ROLE_EU_MANAGER`) | AB'nin her aşaması — `TITLE_EDIT_ROLE_MATRIX` |
 * | YM Yöneticisi (`ROLE_PUB_MANAGER`) | YM'nin her aşaması — `TITLE_EDIT_ROLE_MATRIX` |
 * | Redaksiyon rolleri (28 / 34 / 41 / 42 / 52) | yalnız kendi aşaması |
 * | AYK komite yöneticisi / sekreteri | statü 44 (Ansiklopedi Yönetim Kurulu Kontrol) |
 *
 * Roller `current_user.roles` üzerinden **doğrudan** okunur; `services/currentUser.js`'teki türetilmiş
 * bayraklar (`postOrderTitleRedaction` vb.) bilinçli olarak kullanılmaz — tanımları gereği admin ve
 * `ROLE_EU_MANAGER`'ı da kapsadıkları için matrisin okunurluğunu bozarlar.
 *
 * UI görünürlüğü içindir; gerçek yetkiyi BE zorlar (`PATCH /article_offers/{id}/title` → yetkisizse `403`).
 * Saf/yan etkisizdir; `offer` ya da current_user yokken güvenli şekilde `false` döner.
 *
 * @param {Object|null} offer Madde teklifi (`ArticleOffer`): `status` alanı kullanılır.
 * @returns {ArticleTitleEditPermission} Yetki bayrağı.
 */
export const useArticleTitleEditPermission = (offer) => {
  const { data: current_user } = useGetCurrentUserQuery();
  const { handlePermission } = useUserHasPermission();

  const status = offer?.status ?? null;

  if (!offer || !current_user || status === null) {
    return { canEditTitle: false };
  }

  // Admin/süper admin: statüden bağımsız her aşamada düzenleyebilir (telif öncesi ve terminal statüler dahil).
  const isAdminScope = Boolean(current_user.isAdmin);
  const isRoleScope = (TITLE_EDIT_ROLE_MATRIX[status] ?? []).some((role) => current_user.roles?.includes(role));
  // Statü 44'te yetki role değil AYK komitesindeki göreve bağlıdır: yönetici (`type 0`) veya sekreter (`type 1`).
  const isAykCommitteeScope = status === article_order_status['Ansiklopedi Yonetim Kurulu Kontrol'] && handlePermission(aykId);

  return {
    canEditTitle: isAdminScope || isRoleScope || isAykCommitteeScope,
  };
};
